import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: "Général",
    question: "Qu'est-ce que Jupipotes ?",
    answer: "Jupipotes est une plateforme communautaire décentralisée qui permet aux utilisateurs de se connecter et partager du contenu. Basée sur la technologie blockchain, elle offre une expérience sociale Web3 unique."
  },
  {
    category: "Général",
    question: "Comment créer un compte ?",
    answer: "Pour créer un compte, cliquez sur 'Se connecter' en haut à droite et suivez les étapes de création de profil. Vous pouvez vous connecter avec votre wallet Web3 ou créer un compte traditionnel."
  },
  {
    category: "Compte",
    question: "Comment modifier mon profil ?",
    answer: "Accédez à vos Paramètres depuis le menu de navigation, puis cliquez sur 'Profil'. Vous pourrez y modifier votre photo, votre bio, votre nom d'affichage et d'autres informations."
  },
  {
    category: "Compte",
    question: "Comment sécuriser mon compte ?",
    answer: "Activez l'authentification à deux facteurs (2FA) dans les Paramètres de sécurité. Utilisez un mot de passe fort et unique, et ne partagez jamais vos clés privées."
  },
  {
    category: "Publications",
    question: "Comment publier du contenu ?",
    answer: "Utilisez le bouton 'Créer une publication' sur la page d'accueil. Vous pouvez partager du texte, des images, des vidéos et même des liens YouTube."
  },
  {
    category: "Publications",
    question: "Puis-je modifier ou supprimer mes publications ?",
    answer: "Oui, cliquez sur le menu à trois points sur votre publication et sélectionnez 'Modifier' ou 'Supprimer'."
  },
  {
    category: "Messages",
    question: "Comment envoyer un message privé ?",
    answer: "Visitez le profil d'un utilisateur et cliquez sur 'Message'. Vous pouvez également accéder à vos conversations depuis l'icône Messages dans la navigation."
  },
  {
    category: "Messages",
    question: "Comment savoir si j'ai de nouveaux messages ?",
    answer: "Un badge avec le nombre de messages non lus apparaîtra sur l'icône Messages dans la barre de navigation. Vous recevrez également des notifications en temps réel."
  },
  {
    category: "Fonctionnalités Futures",
    question: "Qu'est-ce que le token JUP ?",
    answer: "Le token JUP est prévu pour 2026. Il permettra la gouvernance décentralisée, les récompenses communautaires et l'accès à des fonctionnalités avancées. Pour l'instant, la plateforme fonctionne sans token."
  },
  {
    category: "Fonctionnalités Futures",
    question: "Comment puis-je contribuer à Jupipotes ?",
    answer: "Vous pouvez contribuer en créant du contenu de qualité, en aidant les nouveaux membres et en partageant vos suggestions d'amélioration. Un système de reconnaissance des contributeurs est en développement."
  },
  {
    category: "Sécurité",
    question: "Mes données sont-elles sécurisées ?",
    answer: "Oui, nous utilisons le chiffrement de bout en bout pour protéger vos données. Les informations sensibles sont stockées de manière sécurisée et nous ne partageons jamais vos données avec des tiers sans votre consentement."
  },
  {
    category: "Sécurité",
    question: "Comment signaler un contenu inapproprié ?",
    answer: "Cliquez sur le menu à trois points sur le contenu concerné et sélectionnez 'Signaler'. Notre équipe de modération examinera votre signalement dans les plus brefs délais."
  },
  {
    category: "Technique",
    question: "Quelles sont les configurations recommandées ?",
    answer: "Jupipotes fonctionne sur tous les navigateurs modernes (Chrome, Firefox, Safari, Edge). Nous recommandons une connexion internet stable et un navigateur à jour pour une meilleure expérience."
  },
  {
    category: "Technique",
    question: "L'application mobile est-elle disponible ?",
    answer: "Jupipotes est actuellement une Progressive Web App (PWA), ce qui signifie que vous pouvez l'utiliser comme une application native sur mobile. Une version native iOS/Android est en cours de développement."
  }
];

const guides = [
  {
    title: "Guide de démarrage rapide",
    description: "Apprenez les bases de Jupipotes en 5 minutes",
    icon: "🚀",
    steps: [
      "Créez votre compte et configurez votre profil",
      "Explorez le fil d'actualité",
      "Publiez votre premier contenu",
      "Connectez-vous avec d'autres membres",
      "Explorez les fonctionnalités avancées"
    ]
  },
  {
    title: "Maximiser votre impact",
    description: "Comment devenir un membre influent de la communauté",
    icon: "💎",
    steps: [
      "Partagez du contenu de qualité et original",
      "Commentez et interagissez de façon constructive",
      "Aidez les nouveaux membres",
      "Participez aux discussions du DAO",
      "Contribuez au développement de la plateforme"
    ]
  }
];

const HelpCenter: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("Tous");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const categories = ["Tous", ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQ = faqData.filter(item => {
    const matchesCategory = selectedCategory === "Tous" || item.category === selectedCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-extrabold text-emerald-100 mb-4">
            Centre d'Aide Jupipotes
          </h1>
          <p className="text-xl text-emerald-200">
            Trouvez toutes les réponses à vos questions
          </p>
        </div>

        {/* Recherche */}
        <div className={`${glassCard} mb-8`}>
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher dans l'aide..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 bg-white/10 border border-emerald-400/30 rounded-xl text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            <svg className="absolute right-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Guides */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-cyan-300 mb-6 flex items-center gap-3">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Guides pratiques
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map((guide, idx) => (
              <div key={idx} className={`${glassCard} hover:border-cyan-400/50 transition-all duration-300`}>
                <div className="text-5xl mb-4">{guide.icon}</div>
                <h3 className="text-xl font-bold text-emerald-100 mb-2">{guide.title}</h3>
                <p className="text-emerald-300 mb-4">{guide.description}</p>
                <ol className="space-y-2">
                  {guide.steps.map((step, stepIdx) => (
                    <li key={stepIdx} className="flex items-start gap-2 text-sm text-emerald-200">
                      <span className="text-cyan-400 font-bold">{stepIdx + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-cyan-300 mb-6 flex items-center gap-3">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Questions fréquentes
          </h2>

          {/* Catégories */}
          <div className="flex flex-wrap gap-3 mb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  selectedCategory === cat
                    ? 'bg-cyan-400/30 text-cyan-200 border-2 border-cyan-400'
                    : 'bg-white/5 text-emerald-300 border border-white/10 hover:bg-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Liste FAQ */}
          <div className="space-y-4">
            {filteredFAQ.map((item, idx) => (
              <div key={idx} className={`${glassCard} hover:border-emerald-400/50 transition-all duration-300`}>
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === idx ? null : idx)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-cyan-400 mb-1 block">{item.category}</span>
                    <h3 className="text-lg font-bold text-emerald-100">{item.question}</h3>
                  </div>
                  <svg
                    className={`h-6 w-6 text-cyan-400 transition-transform duration-300 ${
                      expandedFAQ === idx ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFAQ === idx && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-emerald-200 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFAQ.length === 0 && (
            <div className={`${glassCard} text-center py-12`}>
              <svg className="h-16 w-16 text-emerald-400/50 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xl text-emerald-300">Aucun résultat trouvé</p>
              <p className="text-emerald-200 mt-2">Essayez une autre recherche ou catégorie</p>
            </div>
          )}
        </section>

        {/* Contact Support */}
        <div className={`${glassCard} mt-12 text-center`}>
          <h3 className="text-2xl font-bold text-emerald-100 mb-4">
            Vous ne trouvez pas votre réponse ?
          </h3>
          <p className="text-emerald-200 mb-6">
            Notre équipe de support est là pour vous aider
          </p>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-400/20 text-cyan-200 font-bold rounded-xl hover:bg-cyan-400/40 transition-all duration-300"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contacter le Support
          </Link>
        </div>

        {/* Retour */}
        <div className="mt-8 text-center">
          <Link
            to="/JUPResources"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux ressources
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
